import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@pactfront/ui";

import { commands } from "./network.js";
import { CountryFlag } from "./CountryFlag.js";
import { MetricIcon } from "./MetricIcon.js";
import { preloadMatchScreen } from "./matchScreenLoader.js";
import { useClientStore } from "./store.js";
import { UnitIcon } from "./UnitIcon.js";
import { AnalyticsConsentControl } from "./AnalyticsConsentControl.js";

const powerLevels = [1, 2, 3, 4, 5] as const;

export function Lobby() {
  const { t } = useTranslation();
  const lobby = useClientStore((state) => state.lobby)!;
  const session = useClientStore((state) => state.session);
  const me = lobby.players.find((player) => player.playerId === session?.playerId);
  const host = me?.host === true;
  const modeValue =
    lobby.gameMode.kind === "world-domination"
      ? "world-domination"
      : `turn-limit:${lobby.gameMode.turnLimit}`;
  const targetSeatCount = lobby.players.length + lobby.autoFillCount;
  const computerCountryIndexById = new Map(
    lobby.computers.map((computer, index) => [computer.countryId, index]),
  );

  useEffect(preloadMatchScreen, []);

  const setMode = (value: string) => {
    if (value === "world-domination") {
      void commands.setLobbyMode(lobby.code, { kind: "world-domination" });
      return;
    }
    const turnLimit = Number(value.split(":")[1]) as 10 | 15 | 20 | 30;
    void commands.setLobbyMode(lobby.code, { kind: "turn-limit", turnLimit });
  };

  return (
    <main className="lobby-shell">
      <header className="lobby-header">
        <div className="wordmark">{t("app.brand")}</div>
        <h1 data-testid="lobby-code">{t("lobby.title", { code: lobby.code })}</h1>
      </header>
      <section className="country-list" aria-labelledby="country-heading">
        <div className="country-list-heading">
          <h2 id="country-heading">{t("lobby.country")}</h2>
          <span>{lobby.availableCountries.length}</span>
        </div>
        <div className="country-list-scroll">
          {lobby.availableCountries.map((country) => {
            const selected = me?.countryId === country.countryId;
            const computerIndex = computerCountryIndexById.get(country.countryId);
            const takenByPlayer = country.selectedByPlayerId !== null && !selected;
            const taken = takenByPlayer || computerIndex !== undefined;
            return (
              <button
                key={country.countryId}
                data-testid={`country-${country.countryId}`}
                className={`country-row ${selected ? "is-selected" : ""} ${taken ? "is-taken" : ""} ${computerIndex === undefined ? "" : "is-computer-selected"}`}
                disabled={takenByPlayer}
                aria-pressed={selected}
                onClick={() => void commands.selectCountry(lobby.code, country.countryId)}
                aria-label={t("lobby.countrySummary", {
                  country: t(country.nameKey),
                  power: country.startingPower,
                  development: country.metrics.development,
                  population: country.metrics.population,
                  military: country.metrics.military,
                })}
              >
                <CountryFlag
                  countryId={country.countryId}
                  name={t(country.nameKey)}
                  color={country.color}
                  className="country-row-flag"
                />
                <span className="country-identity">
                  <strong>{t(country.nameKey)}</strong>
                  <span
                    className="country-power"
                    aria-label={t("lobby.power", { power: country.startingPower })}
                  >
                    <span>{t("lobby.powerShort")}</span>
                    <span className="power-levels" aria-hidden="true">
                      {powerLevels.map((level) => (
                        <i
                          key={level}
                          className={level <= country.startingPower ? "is-active" : ""}
                        />
                      ))}
                    </span>
                    <b>{country.startingPower}/5</b>
                  </span>
                </span>
                <span className="country-metrics" aria-hidden="true">
                  <span title={t("lobby.metric.development")}>
                    <MetricIcon metric="development" />
                    <b>{country.metrics.development}</b>
                  </span>
                  <span title={t("lobby.metric.population")}>
                    <MetricIcon metric="population" />
                    <b>{country.metrics.population}</b>
                  </span>
                  <span title={t("lobby.metric.military")}>
                    <UnitIcon unit="infantry" />
                    <b>{country.metrics.military}</b>
                  </span>
                </span>
                {selected || taken ? (
                  <span className="country-status">
                    {selected
                      ? t("lobby.yours")
                      : computerIndex === undefined
                        ? t("lobby.taken")
                        : t("lobby.computer", { count: computerIndex + 1 })}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </section>
      <aside className="seat-list">
        <label className="mode-select">
          <span>{t("lobby.mode")}</span>
          <select
            data-testid="game-mode"
            value={modeValue}
            disabled={!host}
            onChange={(event) => setMode(event.target.value)}
          >
            {[10, 15, 20, 30].map((turnLimit) => (
              <option key={turnLimit} value={`turn-limit:${turnLimit}`}>
                {t("lobby.mode.turnLimit", { count: turnLimit })}
              </option>
            ))}
            <option value="world-domination">{t("lobby.mode.worldDomination")}</option>
          </select>
        </label>
        <div className="seat-list-heading">
          <strong>{t("lobby.players")}</strong>
          <span>
            {lobby.players.length}/{targetSeatCount}
          </span>
        </div>
        {lobby.players.map((player) => (
          <div key={player.playerId} className="seat-row">
            <span>
              {player.name}
              {player.host ? <small>{t("lobby.host")}</small> : null}
            </span>
            <span>{player.ready ? t("lobby.ready") : t("lobby.unready")}</span>
          </div>
        ))}
        {host && lobby.computers.length > 0 ? (
          <Button
            className="bot-reroll"
            data-testid="reroll-computers"
            tone="secondary"
            onClick={() => void commands.rerollComputerCountries(lobby.code)}
          >
            {t("lobby.rerollComputers")}
          </Button>
        ) : null}
        {lobby.computers.map((computer, index) => {
          const country = lobby.availableCountries.find(
            (candidate) => candidate.countryId === computer.countryId,
          );
          return (
            <div
              key={computer.countryId}
              className="seat-row seat-row-bot"
              data-testid={`computer-seat-${index + 1}`}
            >
              <span>
                {country === undefined ? null : (
                  <CountryFlag
                    countryId={country.countryId}
                    name={t(country.nameKey)}
                    color={country.color}
                    className="seat-country-flag"
                  />
                )}
                {t("lobby.computer", { count: index + 1 })}
              </span>
              <span>{country === undefined ? t("lobby.randomCountry") : t(country.nameKey)}</span>
            </div>
          );
        })}
        {lobby.autoFillCount > 0 ? <p className="bot-note">{t("lobby.botNote")}</p> : null}
        <AnalyticsConsentControl compact />
      </aside>
      <footer className="lobby-actions">
        <Button
          data-testid="lobby-ready"
          tone={me?.ready === true ? "secondary" : "primary"}
          disabled={me?.countryId === null}
          onClick={() => void commands.setLobbyReady(lobby.code, me?.ready !== true)}
        >
          {me?.ready === true ? t("lobby.unready") : t("lobby.ready")}
        </Button>
        {host ? (
          <Button
            data-testid="start-match"
            disabled={!lobby.canStart}
            onClick={() => void commands.startLobby(lobby.code)}
          >
            {t("lobby.start")}
          </Button>
        ) : (
          <p>{t("lobby.waiting")}</p>
        )}
      </footer>
    </main>
  );
}
